import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDownRight, Star } from "lucide-react";
import HalloweenScene from "@/components/HalloweenScene";

const HERO_IMG =
  "https://images.unsplash.com/photo-1594477232233-9f77a2c5e39b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const lines = ["A spotless space,", "cleaned with", "a whole lot of care."];

const ease = [0.16, 1, 0.3, 1];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section id="top" ref={ref} className="relative pt-36 pb-20 md:pt-44 md:pb-28 px-6 overflow-hidden" data-testid="hero-section">
      <HalloweenScene />
      <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-10 items-end relative z-10">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease }}
            className="flex items-center gap-3 mb-8"
          >
            <span className="text-xs tracking-[0.25em] uppercase text-[#5A2E14] font-semibold">
              Savannah, GA and surrounding counties · Residential · Commercial · Vacation Rentals
            </span>
          </motion.div>

          <h1 className="font-serif text-[13vw] leading-[0.92] sm:text-7xl lg:text-[5.6rem] tracking-tight text-[#241B2E]">
            {lines.map((line, i) => (
              <span className="line-mask" key={i}>
                <motion.span
                  className="block"
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1, delay: 0.2 + i * 0.14, ease }}
                >
                  {i === 2 ? (
                    <>
                      a whole lot of <span className="italic text-[#C1440E]">care.</span>
                    </>
                  ) : (
                    line
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8, ease }}
            className="mt-8 max-w-md text-[#5A2E14] text-base leading-relaxed"
          >
            Betty Bell's brings a warm, meticulous touch to every home, office and
            vacation rental. Family-run, detail-obsessed, and always on time.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.95, ease }}
            className="mt-10 flex flex-wrap items-center gap-5"
          >
            <button
              onClick={() => (window.lenis ? window.lenis.scrollTo("#contact", { offset: -80 }) : document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" }))}
              data-testid="hero-cta"
              className="group inline-flex items-center gap-2 pl-6 pr-2 py-2 rounded-full bg-[#C1440E] text-white hover:-translate-y-1 transition-transform duration-300"
            >
              <span className="text-sm font-medium">Book your clean</span>
              <span className="grid place-items-center w-9 h-9 rounded-full bg-[#241B2E] group-hover:rotate-45 transition-transform duration-300">
                <ArrowDownRight size={16} />
              </span>
            </button>
            <div className="flex items-center gap-2 text-[#5A2E14]">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={15} className="fill-[#C1440E] text-[#C1440E]" />
                ))}
              </div>
              <span className="text-sm">Loved by local families</span>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.4, ease }}
          className="lg:col-span-5 relative"
          style={{ opacity: fade }}
        >
          <div className="relative overflow-hidden rounded-[2rem] border border-[#C68A3A] aspect-[4/5] shadow-[0_30px_60px_-25px_rgba(107,66,38,0.5)]">
            <motion.img
              src={HERO_IMG}
              alt="A historic Savannah, GA home with a classic staircase"
              style={{ y: imgY, scale: imgScale }}
              className="w-full h-[115%] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#241B2E]/25 to-transparent" />
          </div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.1, ease }}
            className="absolute -bottom-6 -left-6 bg-[#F7E9CC] border border-[#C68A3A] rounded-2xl px-5 py-4 backdrop-blur-xl"
            data-testid="hero-badge"
          >
            <p className="font-serif text-4xl text-[#C1440E] leading-none">12+</p>
            <p className="text-xs text-[#5A2E14] mt-1">years making spaces shine</p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
